import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { productEffectivePrice } from '../productPricing';
import { customizationUnitPriceDelta } from '../jewelry/jewelryCustomization';
import { cartApiPost, cartLinePayload, serverRowsToCartLines } from './cartApi';
import {
  cartItemCount,
  cartLineKey,
  cartSubtotal,
  isOutOfStock,
  lineMaxQty,
  normalizeStockQty,
  readCartFromStorage,
  stockLimitMessage,
  writeCartToStorage,
} from './cartUtils';

const CartContext = createContext(null);
const CART_SAVE_DEBOUNCE_MS = 700;

function capLineQty(line, qty) {
  const max = lineMaxQty(line.stockQty);
  if (max <= 0) return 0;
  return Math.min(max, Math.max(1, Math.floor(qty)));
}

export function CartProvider({ children }) {
  const { ready, isAuthed, token } = useAuth();
  const [lines, setLines] = useState(() => readCartFromStorage());
  const [toast, setToast] = useState(null);
  const [serverEnabled, setServerEnabled] = useState(true);
  const [cartLoading, setCartLoading] = useState(false);
  const syncedTokenRef = useRef('');
  const skipServerSaveRef = useRef(false);
  const saveTimerRef = useRef(null);

  const persistLocal = useCallback((next) => {
    setLines(next);
    writeCartToStorage(next);
  }, []);

  const loadFromServer = useCallback(
    async (mergeLocal = false) => {
      if (!isAuthed) {
        setLines(readCartFromStorage());
        return;
      }
      setCartLoading(true);
      skipServerSaveRef.current = true;
      try {
        const local = mergeLocal ? readCartFromStorage() : [];
        if (mergeLocal && local.length > 0) {
          const syncRes = await cartApiPost('sync_cart', {
            items: local.map(cartLinePayload),
          });
          if (!syncRes.ok) {
            setLines(local);
            return;
          }
          setServerEnabled(syncRes.data?.enabled !== false);
          setLines(serverRowsToCartLines(syncRes.data?.items));
          writeCartToStorage([]);
          return;
        }
        const res = await cartApiPost('list_cart');
        if (!res.ok) {
          if (mergeLocal) setLines(local);
          return;
        }
        setServerEnabled(res.data?.enabled !== false);
        const fromServer = serverRowsToCartLines(res.data?.items);
        if (fromServer.length > 0) {
          setLines(fromServer);
          writeCartToStorage(fromServer);
        } else if (local.length > 0) {
          setLines(local);
        } else {
          setLines([]);
          writeCartToStorage([]);
        }
      } finally {
        skipServerSaveRef.current = false;
        setCartLoading(false);
      }
    },
    [isAuthed]
  );

  useEffect(() => {
    if (!ready) return undefined;
    if (!isAuthed) {
      syncedTokenRef.current = '';
      setLines(readCartFromStorage());
      return undefined;
    }
    const tok = String(token || '').trim();
    if (!tok || syncedTokenRef.current === tok) return undefined;
    syncedTokenRef.current = tok;
    void loadFromServer(true);
    return undefined;
  }, [ready, isAuthed, token, loadFromServer]);

  useEffect(() => {
    if (!ready || !isAuthed || !serverEnabled || skipServerSaveRef.current) {
      return undefined;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void cartApiPost('save_cart', { items: lines.map(cartLinePayload) });
    }, CART_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [lines, ready, isAuthed, serverEnabled]);

  useEffect(() => {
    writeCartToStorage(lines);
  }, [lines]);

  const addItem = useCallback((product, categorySlug, qty = 1, customization = '') => {
    const custom = String(customization || '').trim();
    const price = productEffectivePrice(product) + customizationUnitPriceDelta(custom, product);
    if (price <= 0) return { ok: false, error: 'This item needs a custom quote. Contact us on WhatsApp.' };
    const productId = String(product?.id || '').trim();
    const cat = String(categorySlug || '').trim();
    if (!productId || !cat) return { ok: false, error: 'Invalid product.' };

    const stockQty = normalizeStockQty(product?.stock_qty);
    const name = String(product?.name || productId);
    if (isOutOfStock(stockQty)) {
      return { ok: false, error: stockLimitMessage(name, 0) };
    }
    const max = lineMaxQty(stockQty);
    const amount = Math.max(1, Math.min(max, Number(qty) || 1));
    const key = cartLineKey(cat, productId, custom);
    const image = product?.image || (Array.isArray(product?.images) ? product.images[0] : '') || '';

    let limitError = '';
    setLines((prev) => {
      const idx = prev.findIndex(
        (l) => cartLineKey(l.categorySlug, l.productId, l.customization) === key
      );
      if (idx >= 0) {
        const wouldBe = prev[idx].qty + amount;
        if (wouldBe > max) {
          limitError = stockLimitMessage(name, max);
          return prev;
        }
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          name,
          price,
          image: String(image || next[idx].image || ''),
          stockQty,
          qty: wouldBe,
        };
        return next;
      }
      return [
        ...prev,
        {
          categorySlug: cat,
          productId,
          name,
          price,
          image: String(image || ''),
          stockQty,
          customization: custom,
          qty: amount,
        },
      ];
    });

    if (limitError) return { ok: false, error: limitError };

    setToast({ productName: name });
    return { ok: true };
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const setQty = useCallback((categorySlug, productId, qty, customization = '') => {
    const key = cartLineKey(categorySlug, productId, customization);
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 1) {
      setLines((prev) =>
        prev.filter((l) => cartLineKey(l.categorySlug, l.productId, l.customization) !== key)
      );
      return;
    }
    setLines((prev) =>
      prev.map((l) => {
        if (cartLineKey(l.categorySlug, l.productId, l.customization) !== key) return l;
        return { ...l, qty: capLineQty(l, n) };
      })
    );
  }, []);

  const removeItem = useCallback((categorySlug, productId, customization = '') => {
    const key = cartLineKey(categorySlug, productId, customization);
    setLines((prev) =>
      prev.filter((l) => cartLineKey(l.categorySlug, l.productId, l.customization) !== key)
    );
  }, []);

  const clearCart = useCallback(() => {
    setLines([]);
    if (isAuthed && serverEnabled) {
      void cartApiPost('save_cart', { items: [] });
    }
  }, [isAuthed, serverEnabled]);

  const reconcileStock = useCallback((stockByKey) => {
    if (!stockByKey || stockByKey.size === 0) return { removed: [], adjusted: [] };

    const removed = [];
    const adjusted = [];
    let changed = false;

    setLines((prev) => {
      const next = prev
        .map((line) => {
          const baseKey = cartLineKey(line.categorySlug, line.productId);
          if (!stockByKey.has(baseKey)) return line;

          const stockQty = stockByKey.get(baseKey);
          const max = lineMaxQty(stockQty);
          if (max <= 0) {
            removed.push(line.name);
            changed = true;
            return null;
          }
          const capped = Math.min(line.qty, max);
          if (capped < line.qty) adjusted.push(line.name);
          if (capped !== line.qty || line.stockQty !== stockQty) changed = true;
          return { ...line, stockQty, qty: capped };
        })
        .filter(Boolean);

      return changed ? next : prev;
    });

    return { removed, adjusted };
  }, []);

  const value = useMemo(
    () => ({
      lines,
      itemCount: cartItemCount(lines),
      subtotal: cartSubtotal(lines),
      addItem,
      setQty,
      removeItem,
      clearCart,
      reconcileStock,
      toast,
      dismissToast,
      cartLoading,
      serverCartEnabled: serverEnabled,
      reloadCart: () => loadFromServer(false),
    }),
    [
      lines,
      toast,
      addItem,
      setQty,
      removeItem,
      clearCart,
      reconcileStock,
      dismissToast,
      cartLoading,
      serverEnabled,
      loadFromServer,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
